import type { Identifier } from '@medplum/fhirtypes';
import { IDENTIFIER_SYSTEMS } from './identifiers';

/**
 * The device health store a sample originated from. The value doubles as the namespace prefix on a
 * {@link IDENTIFIER_SYSTEMS.HEALTH_SAMPLE} identifier value, since the same underlying reading gets
 * distinct source ids on each platform.
 */
export type HealthSamplePlatform = 'healthkit' | 'healthconnect';

/** The health store's own name, as a person knows it — shown verbatim, never translated. */
export const HEALTH_SAMPLE_PLATFORM_NAMES: Record<HealthSamplePlatform, string> = {
  healthkit: 'Apple Health',
  healthconnect: 'Health Connect',
};

/**
 * The platform a `HEALTH_SAMPLE` identifier value names, read back from its `<platform>:<sourceId>`
 * prefix. Returns undefined for a value written by something else, so callers can fall through
 * rather than mislabel a reading's origin.
 */
export const parseHealthSamplePlatform = (value: string | undefined): HealthSamplePlatform | undefined => {
  // Bail on a value with no separator rather than letting `indexOf`'s -1 reach `slice`, which would
  // drop the last character and read "healthkitX" as the healthkit platform.
  const separator = value?.indexOf(':') ?? -1;
  if (value === undefined || separator === -1) return undefined;
  const platform = value.slice(0, separator);
  return platform === 'healthkit' || platform === 'healthconnect' ? platform : undefined;
};

/**
 * Derive the platform-namespaced identifier value for a device health sample.
 *
 * `sourceId` is the stable per-sample id from the source store — a HealthKit sample UUID or a
 * Health Connect `record.metadata.id`. Prefixing with the platform keeps the two stores' id spaces
 * from colliding and records device-sync provenance in the value itself.
 */
export const deriveHealthSampleIdentifierValue = (platform: HealthSamplePlatform, sourceId: string): string =>
  `${platform}:${sourceId}`;

/**
 * Per-day `sourceId` for an aggregate metric — e.g. `'steps:2026-07-06'`. Prefixed with the platform
 * (via {@link deriveHealthSampleIdentifierValue} / {@link buildHealthSampleIdentifier}) it yields the
 * synthesized identity `(patient [via access policy], metric, localDate, source)` so a resync of the
 * same day overwrites that day's total through the conditional PUT instead of creating a sibling.
 */
export const deriveAggregateDaySourceId = (metric: string, localDate: string): string => `${metric}:${localDate}`;

/** Build the {@link IDENTIFIER_SYSTEMS.HEALTH_SAMPLE} `Identifier` for a device health sample. */
export const buildHealthSampleIdentifier = (platform: HealthSamplePlatform, sourceId: string): Identifier => ({
  system: IDENTIFIER_SYSTEMS.HEALTH_SAMPLE,
  value: deriveHealthSampleIdentifierValue(platform, sourceId),
});

/**
 * The `system|value` token selecting the Observation carrying a given `HEALTH_SAMPLE` identifier
 * value — the form an `identifier` search param takes in a Medplum search params object.
 */
export const healthSampleIdentifierToken = (identifierValue: string): string =>
  `${IDENTIFIER_SYSTEMS.HEALTH_SAMPLE}|${identifierValue}`;

/**
 * FHIR search criteria selecting the Observation carrying a given `HEALTH_SAMPLE` identifier value —
 * the uniqueness key for the conditional `PUT` (`observation.upsert` / `observation.retract`).
 */
export const healthSampleSearchCriteria = (identifierValue: string): string =>
  `identifier=${healthSampleIdentifierToken(identifierValue)}`;
